import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Download, Trash2, X, Globe, Link as LinkIcon, Lock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useStreaming } from "@/lib/streaming";

function fmtSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0, n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
function fmtDuration(ms) {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (v) => String(v).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

/**
 * Post-stream review modal — opens automatically when the streamer ends
 * their stream and a recording exists in the provider. Offers Save (to
 * server-side VOD storage), Download (local file), or Delete (discard).
 */
export default function StreamRecordingModal() {
  const streaming = useStreaming();
  const nav = useNavigate();
  const rec = streaming.pendingRecording;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const defaultTitle = useMemo(() => {
    const d = new Date();
    return `Stream — ${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }, []);

  if (!rec) return null;

  const doDownload = () => {
    if (!rec.url) return;
    const a = document.createElement("a");
    a.href = rec.url;
    const t = (title || defaultTitle).replace(/[/\\?%*:|"<>]+/g, "-").slice(0, 60);
    a.download = `${t}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Download started", { description: "Check your browser downloads." });
  };

  const doDelete = () => {
    streaming.dismissPendingRecording();
    toast("Recording discarded");
  };

  const doSave = async () => {
    if (busy) return;
    setBusy(true);
    setUploadPct(0);
    try {
      const form = new FormData();
      const finalTitle = (title || defaultTitle).slice(0, 120);
      const filename = `${finalTitle.replace(/[/\\?%*:|"<>]+/g, "-").slice(0, 60)}.webm`;
      form.append("file", rec.blob, filename);
      form.append("title", finalTitle);
      form.append("channel_username", rec.channelUsername || "");
      form.append("duration_ms", String(rec.durationMs || 0));
      form.append("visibility", visibility);
      if (description) form.append("description", description);
      const resp = await api.post("/streams/recordings", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total) setUploadPct(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      const saved = resp.data;
      toast.success("Stream saved!", {
        description: `Visibility: ${visibility}. View it in your Past streams.`,
        action: rec.channelUsername
          ? { label: "View", onClick: () => nav(`/c/${rec.channelUsername}?tab=past`) }
          : undefined,
      });
      // Free the blob and close
      streaming.dismissPendingRecording();
      return saved;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed";
      toast.error("Couldn't save your stream", { description: String(detail).slice(0, 200) });
    } finally {
      setBusy(false);
      setUploadPct(0);
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      data-testid="stream-recording-modal"
    >
      <div className="w-full max-w-2xl bg-[#0c0c0e] border border-[color:var(--sc-cyan)]/40 rounded-md shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="sc-label text-[color:var(--sc-cyan)] tracking-widest">Stream ended</div>
            <div className="sc-heading text-xl font-bold mt-0.5">Save your recording?</div>
          </div>
          <button
            data-testid="stream-recording-close"
            onClick={doDelete}
            disabled={busy}
            className="p-1.5 rounded-sm border border-white/10 text-zinc-400 hover:border-white/30 hover:text-white disabled:opacity-40"
            title="Discard without saving"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 grid md:grid-cols-2 gap-5 overflow-y-auto">
          {/* Preview */}
          <div>
            <div className="sc-label mb-1.5">Preview</div>
            <video
              data-testid="stream-recording-preview"
              src={rec.url}
              controls
              className="w-full aspect-video bg-black rounded-sm border border-white/10"
            />
            <div className="mt-2 flex items-center gap-3 text-[10px] sc-mono uppercase tracking-widest text-zinc-500">
              <span data-testid="stream-recording-duration">{fmtDuration(rec.durationMs)}</span>
              <span>·</span>
              <span data-testid="stream-recording-size">{fmtSize(rec.sizeBytes)}</span>
              <span>·</span>
              <span>webm</span>
            </div>
          </div>

          {/* Metadata form */}
          <div className="space-y-4">
            <div>
              <label className="sc-label block mb-1.5">Title</label>
              <input
                data-testid="stream-recording-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder={defaultTitle}
                className="sc-input w-full text-sm"
              />
            </div>
            <div>
              <label className="sc-label block mb-1.5">Description (optional)</label>
              <textarea
                data-testid="stream-recording-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
                rows={3}
                className="sc-input w-full text-sm resize-none"
                placeholder="What did viewers see today?"
              />
            </div>
            <div>
              <div className="sc-label mb-2">Visibility</div>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { v: "public",   Icon: Globe,     label: "Public",   hint: "Anyone can watch" },
                  { v: "unlisted", Icon: LinkIcon,  label: "Unlisted", hint: "Only via link" },
                  { v: "private",  Icon: Lock,      label: "Private",  hint: "Only you" },
                ].map(({ v, Icon, label, hint }) => (
                  <button
                    key={v}
                    data-testid={`stream-recording-visibility-${v}`}
                    onClick={() => setVisibility(v)}
                    disabled={busy}
                    className={`p-2 rounded-sm border text-[10px] sc-mono uppercase tracking-widest flex flex-col items-center gap-1 ${visibility === v ? "border-[color:var(--sc-cyan)] bg-[color:var(--sc-cyan)]/10 text-[color:var(--sc-cyan)]" : "border-white/10 text-zinc-500 hover:border-white/25"}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                    <span className="text-[8px] normal-case text-zinc-500">{hint}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/10 flex flex-col-reverse sm:flex-row gap-2">
          <button
            data-testid="stream-recording-delete"
            onClick={doDelete}
            disabled={busy}
            className="sc-btn-ghost flex items-center justify-center gap-2 disabled:opacity-50 text-[color:var(--sc-pink)] border-[color:var(--sc-pink)]/40"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <div className="flex-1" />
          <button
            data-testid="stream-recording-download"
            onClick={doDownload}
            disabled={busy}
            className="sc-btn-ghost flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Download
          </button>
          <button
            data-testid="stream-recording-save"
            onClick={doSave}
            disabled={busy}
            className="sc-btn flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {busy ? (uploadPct > 0 ? `Uploading ${uploadPct}%` : "Uploading…") : "Save to my channel"}
          </button>
        </div>
      </div>
    </div>
  );
}
